// 進階能力簇設定（APP#40）：Siren 0x8081 / Audio 0x8080 / Floodlight 0x8072 /
// Doorbell 0x8030。此四簇前此列為 KNOWN_BUT_UNRENDERED（web-v1 未做）；lsappv3 有
// 對應功能（警笛 / mic·spk 音量 / 補光燈 / 門鈴），本檔補齊為 capability-driven section。
//   - member id 全取自 as-built capability_dictionary.json v1.6.0（spec-ssot-discipline，
//     never invent ids）。dictionary 未定義者不渲染。
//   - 讀寫走既有 useAttr（op:get on mount + 樂觀 op:set + rollback）；CMD 走 opCmd。
//   - authz：本區塊全為 SET widget，故整段由 DeviceSettings 依 21 §2.1 的 permission gate
//     決定渲不渲染（無 `control` → 不渲染，非 disable）；server fail-closed 仍為權威。

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AUDIO_MIC_VOL,
  AUDIO_MUTE_SPEAKER,
  AUDIO_SPEAKER_VOL,
  DOORBELL_CHIME_TIME,
  DOORBELL_CHIME_TYPE,
  FLOOD_BRIGHTNESS,
  FLOOD_COLOR_TEMP,
  FLOOD_LIGHT_WARN,
  FLOOD_SWITCH,
  SIREN_ALARM,
  SIREN_DURATION,
  SIREN_TRIGGER,
  SIREN_VOLUME,
} from './ids';
import { opCmd, useAttr } from './ops';
import { ConfirmDialog, SectionCard, SegmentedRow, SliderRow, ToggleRow } from './widgets';

function Connecting({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <SectionCard title={title}>
      <p className="text-secondary" style={{ margin: 0 }}>
        {t('deviceSettings.connecting')}
      </p>
    </SectionCard>
  );
}

/** 警報器（0x8081）：總開關 / 音量 / 時長 + 手動觸發（CMD，需確認）。 */
export function SirenSection({ deviceId, wsOpen }: { deviceId: string; wsOpen: boolean }) {
  const { t } = useTranslation();
  const alarm = useAttr(deviceId, SIREN_ALARM, wsOpen);
  const volume = useAttr(deviceId, SIREN_VOLUME, wsOpen);
  const duration = useAttr(deviceId, SIREN_DURATION, wsOpen);
  const [confirmTrigger, setConfirmTrigger] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);

  if (!wsOpen) return <Connecting title={t('deviceSettings.siren')} />;

  async function runTrigger() {
    setTriggering(true);
    setTriggerError(null);
    try {
      await opCmd(deviceId, SIREN_TRIGGER);
      setConfirmTrigger(false);
    } catch (err) {
      setTriggerError(err instanceof Error ? err.message : t('deviceSettings.setFailed'));
    } finally {
      setTriggering(false);
    }
  }

  return (
    <SectionCard title={t('deviceSettings.siren')}>
      <ToggleRow label={t('deviceSettings.alarmFunction')} attr={alarm} />
      <SliderRow label={t('deviceSettings.sirenVolume')} attr={volume} min={0} max={100} />
      <SliderRow label={t('deviceSettings.sirenDuration')} attr={duration} min={5} max={120} unit={t('deviceSettings.unitSeconds')} />
      {(
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 'var(--spacing-4)' }}>
          <button className="btn-danger" disabled={triggering} onClick={() => setConfirmTrigger(true)}>
            {t('deviceSettings.triggerSiren')}
          </button>
        </div>
      )}
      {triggerError && (
        <p className="error-text" role="alert" style={{ margin: 0 }}>
          {triggerError}
        </p>
      )}
      {confirmTrigger && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.triggerSirenConfirmTitle'),
            body: t('deviceSettings.triggerSirenConfirmBody'),
            confirmLabel: t('deviceSettings.triggerSiren'),
            danger: true,
          }}
          busy={triggering}
          onCancel={() => setConfirmTrigger(false)}
          onConfirm={() => void runTrigger()}
        />
      )}
    </SectionCard>
  );
}

/** 音訊（0x8080）：喇叭 / 麥克風音量 + 靜音。 */
export function AudioSection({ deviceId, wsOpen }: { deviceId: string; wsOpen: boolean }) {
  const { t } = useTranslation();
  const speaker = useAttr(deviceId, AUDIO_SPEAKER_VOL, wsOpen);
  const mic = useAttr(deviceId, AUDIO_MIC_VOL, wsOpen);
  const mute = useAttr(deviceId, AUDIO_MUTE_SPEAKER, wsOpen);

  if (!wsOpen) return <Connecting title={t('deviceSettings.audio')} />;

  // spec 21 §3.6：靜音時 SpeakerVolume slider disable（值保留）。
  const muted = mute.value === true || mute.value === 1;

  return (
    <SectionCard title={t('deviceSettings.audio')}>
      <SliderRow label={t('deviceSettings.speakerVolume')} attr={speaker} min={0} max={100} disabled={muted} />
      <SliderRow label={t('deviceSettings.micVolume')} attr={mic} min={0} max={100} />
      <ToggleRow label={t('deviceSettings.muteSpeaker')} attr={mute} />
    </SectionCard>
  );
}

/** 補光燈 / 燈泡（0x8072）：開關 / 亮度 / 色溫 / 偵測聯動亮燈。 */
export function FloodlightSection({ deviceId, wsOpen }: { deviceId: string; wsOpen: boolean }) {
  const { t } = useTranslation();
  const sw = useAttr(deviceId, FLOOD_SWITCH, wsOpen);
  const brightness = useAttr(deviceId, FLOOD_BRIGHTNESS, wsOpen);
  const colorTemp = useAttr(deviceId, FLOOD_COLOR_TEMP, wsOpen);
  const lightWarn = useAttr(deviceId, FLOOD_LIGHT_WARN, wsOpen);

  if (!wsOpen) return <Connecting title={t('deviceSettings.floodlight')} />;

  // spec 21 §3.5：BulbSwitch=false 時 Brightness slider disable。
  const bulbOn = sw.value === true || sw.value === 1;

  return (
    <SectionCard title={t('deviceSettings.floodlight')}>
      <ToggleRow label={t('deviceSettings.bulbSwitch')} attr={sw} />
      <SliderRow label={t('deviceSettings.brightness')} attr={brightness} min={0} max={100} disabled={!bulbOn} />
      <SliderRow label={t('deviceSettings.colorTemperature')} attr={colorTemp} min={2200} max={5000} unit="K" />
      <ToggleRow label={t('deviceSettings.lightWarn')} hint={t('deviceSettings.lightWarnHint')} attr={lightWarn} />
    </SectionCard>
  );
}

/** 門鈴 / 響鈴（0x8030）：響鈴類型（機械 / 電子）+ 響鈴時長。 */
export function DoorbellSection({ deviceId, wsOpen }: { deviceId: string; wsOpen: boolean }) {
  const { t } = useTranslation();
  const chimeType = useAttr(deviceId, DOORBELL_CHIME_TYPE, wsOpen);
  const chimeTime = useAttr(deviceId, DOORBELL_CHIME_TIME, wsOpen);

  if (!wsOpen) return <Connecting title={t('deviceSettings.doorbell')} />;

  return (
    <SectionCard title={t('deviceSettings.doorbell')}>
      <SegmentedRow
        label={t('deviceSettings.chimeType')}
        attr={chimeType}
       
        options={[
          [0, t('deviceSettings.chimeTypeMechanical')],
          [1, t('deviceSettings.chimeTypeElectronic')],
        ]}
      />
      <SliderRow label={t('deviceSettings.chimeTime')} hint={t('deviceSettings.chimeTimeHint')} attr={chimeTime} min={0} max={120} unit={t('deviceSettings.unitSeconds')} />
    </SectionCard>
  );
}
